import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Loader2, Megaphone, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function ContentPanel() {
  const [ann, setAnn] = useState(null);
  const [disc, setDisc] = useState(null);
  const [saving, setSaving] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/content-settings`, { withCredentials: true })
      .then((r) => { setAnn(r.data.opening_announcement); setDisc(r.data.discovery_banner); })
      .catch(() => toast.error('Impossible de charger le contenu.'));
  }, []);

  const saveAnn = async () => {
    setSaving('ann');
    try {
      const r = await axios.put(`${API}/admin/content-settings/announcement`,
        { enabled: ann.enabled, title: ann.title, message: ann.message }, { withCredentials: true });
      setAnn(r.data);
      toast.success('Annonce enregistrée — visible dans les notifications des clientes.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur.');
    } finally { setSaving(''); }
  };

  const saveDisc = async () => {
    setSaving('disc');
    try {
      const r = await axios.put(`${API}/admin/content-settings/discovery`,
        { enabled: disc.enabled, title: disc.title, message: disc.message, start_date: disc.start_date || '', end_date: disc.end_date || '' },
        { withCredentials: true });
      setDisc(r.data);
      toast.success('Bannière découverte enregistrée.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur.');
    } finally { setSaving(''); }
  };

  if (!ann || !disc) {
    return <Card className="bg-white border-[#E9DDCC] rounded-2xl"><CardContent className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></CardContent></Card>;
  }

  return (
    <div className="space-y-6" data-testid="content-panel">
      {/* Annonce ouverture */}
      <Card className="bg-white border-[#E9DDCC] rounded-2xl">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-[#493329] flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#C99A55]" /> Notification d'ouverture
          </CardTitle>
          <p className="text-xs text-[#7A6455]">Affichée dans le centre de notifications de chaque cliente. Aucun e-mail envoyé.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-[#493329]">Annonce activée</Label>
            <Switch checked={ann.enabled} onCheckedChange={(v) => setAnn({ ...ann, enabled: v })} data-testid="announcement-enabled" />
          </div>
          <div>
            <Label className="text-sm text-[#493329]">Titre</Label>
            <Input value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} className="mt-1" data-testid="announcement-title" />
          </div>
          <div>
            <Label className="text-sm text-[#493329]">Message</Label>
            <Textarea value={ann.message} onChange={(e) => setAnn({ ...ann, message: e.target.value })} rows={3} className="mt-1" data-testid="announcement-message" />
          </div>
          <Button onClick={saveAnn} disabled={saving === 'ann'} className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-full" data-testid="announcement-save">
            {saving === 'ann' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer l\'annonce'}
          </Button>
        </CardContent>
      </Card>

      {/* Bannière découverte */}
      <Card className="bg-white border-[#E9DDCC] rounded-2xl">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-[#493329] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C99A55]" /> Bannière « Première semaine découverte »
          </CardTitle>
          <p className="text-xs text-[#7A6455]">Bannière d'information affichée sur le planning. Aucune mécanique de réservation.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-[#493329]">Bannière activée</Label>
            <Switch checked={disc.enabled} onCheckedChange={(v) => setDisc({ ...disc, enabled: v })} data-testid="discovery-enabled" />
          </div>
          <div>
            <Label className="text-sm text-[#493329]">Titre</Label>
            <Input value={disc.title} onChange={(e) => setDisc({ ...disc, title: e.target.value })} className="mt-1" data-testid="discovery-title" />
          </div>
          <div>
            <Label className="text-sm text-[#493329]">Message</Label>
            <Textarea value={disc.message} onChange={(e) => setDisc({ ...disc, message: e.target.value })} rows={2} className="mt-1" data-testid="discovery-message" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#493329]">Début (optionnel)</Label>
              <Input type="date" value={disc.start_date || ''} onChange={(e) => setDisc({ ...disc, start_date: e.target.value })} className="mt-1" data-testid="discovery-start" />
            </div>
            <div>
              <Label className="text-sm text-[#493329]">Fin (optionnel)</Label>
              <Input type="date" value={disc.end_date || ''} onChange={(e) => setDisc({ ...disc, end_date: e.target.value })} className="mt-1" data-testid="discovery-end" />
            </div>
          </div>
          <Button onClick={saveDisc} disabled={saving === 'disc'} className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-full" data-testid="discovery-save">
            {saving === 'disc' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer la bannière'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
